/**
 * Pure bulk-selection helpers shared across the CRM's list pages. Two families,
 * because the pages keep their selection in two different shapes:
 *
 *   • A `Set<Id>` of ids (`Id` = string | number) — File Manager + Invoices
 *     (string ids) and Contacts (numeric ids). The header checkbox only needs
 *     ids; bulk actions look the rows back up. Functions: `toggleId`,
 *     `addIdRange`, `collectIds`, `allSelected`, `isIndeterminate`.
 *   • An array of the selected ROW objects, deduped by id — Prospects (its bulk
 *     actions need the row objects, not just their ids). Functions: `toggleItem`,
 *     `isItemSelected`, `allItemsSelected`.
 *
 * Every function is pure: it returns a NEW set / array / boolean and never
 * mutates its input or touches React state (the page keeps the `setX` wiring).
 * The "all / indeterminate" predicates take COUNTS (`size` / `length`) so both
 * families share one definition. No money / reseller here — wall-neutral.
 */

/* ── Set<Id> family (File Manager, Invoices, Contacts) ───────────────────── */

/** Toggle one id in the selection, returning a new set (never mutates input). */
export function toggleId<Id extends string | number>(selected: Set<Id>, id: Id): Set<Id> {
  const next = new Set(selected);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return next;
}

/**
 * Add every id in the inclusive index range between `indexA` and `indexB` of
 * `items` to the selection — the shift-click "select range" gesture. The two
 * indices may be given in either order; out-of-bounds / missing slots are
 * skipped. Returns a NEW set (never mutates input).
 */
export function addIdRange<T, Id>(
  selected: Set<Id>,
  items: T[],
  indexA: number,
  indexB: number,
  keyFn: (item: T) => Id,
): Set<Id> {
  const start = Math.min(indexA, indexB);
  const end = Math.max(indexA, indexB);
  const next = new Set(selected);
  for (let i = start; i <= end; i++) {
    const item = items[i];
    if (item !== undefined) {
      next.add(keyFn(item));
    }
  }
  return next;
}

/** Every item's id (for "select all"); `keyFn` extracts the id. */
export function collectIds<T, Id extends string | number>(items: T[], keyFn: (item: T) => Id): Set<Id> {
  return new Set(items.map(keyFn));
}

/** True when there are rows and every one is selected (the header checkbox). */
export function allSelected(selectedCount: number, totalCount: number): boolean {
  return selectedCount > 0 && selectedCount === totalCount;
}

/** True for a partial selection — drives the header checkbox's indeterminate state. */
export function isIndeterminate(selectedCount: number, totalCount: number): boolean {
  return selectedCount > 0 && selectedCount < totalCount;
}

/* ── array-by-id family (Prospects) ──────────────────────────────────────── */

/** Is this row already in the selection (matched by `keyFn`, not reference)? */
export function isItemSelected<T>(selected: T[], item: T, keyFn: (item: T) => string | number): boolean {
  const key = keyFn(item);
  return selected.some((candidate) => keyFn(candidate) === key);
}

/** Add the row if absent, remove it if present (by `keyFn`) — returns a NEW array. */
export function toggleItem<T>(selected: T[], item: T, keyFn: (item: T) => string | number): T[] {
  const key = keyFn(item);
  return isItemSelected(selected, item, keyFn)
    ? selected.filter((candidate) => keyFn(candidate) !== key)
    : [...selected, item];
}

/** True when there are rows and every visible one is selected (header checkbox). */
export function allItemsSelected<T>(selected: T[], items: T[]): boolean {
  return items.length > 0 && selected.length === items.length;
}
